import create from './5-libs/core';
import modules from './2-tool/modules.jsx';

// 2-tool (utilities)
import './2-tool/Typography';

// 3-generics
import './3-generics/LazyLoadImage';

// 4-helper (helper classes)
import './4-helper/BrowserDetection';
import './4-helper/CheckDevice';
import './4-helper/ConvertSvg';
import './4-helper/UserAgent';
import './4-helper/ADA';
import './4-helper/BackgroundVideo';
import './4-helper/ScrollAnchorLink';

// 5-libs
import './5-libs/Popup';
import './5-libs/SelectC8';
import './5-libs/GravityForm';

// Components (co-located structure)
import '../../components/animation-page/script.js'
import '../../components/header/scripts/Header.js'
import '../../components/header/scripts/Menu.js'
import '../../components/header/scripts/ADAMenu.js'
import '../../components/header/scripts/SearchLightbox.js'
import '../../components/module-info-settings/script.js'

// Modules
import '../../modules/basic/script.js'
import '../../modules/form-module/FormModule.js'

// Heavy, below-the-fold visual modules (carousels / sliders / galleries / stat
// counters / native-event sections). Instead of importing + initialising every
// one at once on DOMContentLoaded — a single long main-thread task that inflates
// Total Blocking Time — these are initialised when they approach the viewport
// (IntersectionObserver). Elements already on screen still init on the observer's
// first tick, so nothing above the fold is delayed; the rest run on scroll, out
// of the initial load window. Light / interactive / form / nav / filter modules
// stay eager below so their timing is unchanged.
const DEFER_MODULES = new Set([
  'ModSlider', 'ModCarouselSlide', 'BasicModule', 'LogoSlide',
  'TabContentTextAndImage', 'TabContentListing', 'TestimonialsCarousel',
  'ResultCarousel', 'ContentWithNumber', 'BrandLogo', 'IconCards',
  'UpcomingEvents', 'Testimonial', 'CardLarge', 'CloudTransformationResources',
  'FlipCards', 'PartnerCaseStudies', 'TeamPortraits', 'VideoGridContent',
  'StatProgressBars', 'StatRings', 'CultureGallery', 'VideoPlayer',
  'NativeEventWhyTechx', 'NativeEventTabs', 'NativeEventKeynote',
  'NativeEventPanelists', 'NativeEventAgenda', 'NativeEventFaq',
  'NativeEventAiCertification',
]);

// Import + init one deferred module on a single node (mirrors core/create's
// per-element behaviour, honouring the data-mod-init guard and organism.render).
async function initDeferredModuleOnNode(organism, node) {
  if (node.dataset.modInit === 'true') return;
  node.dataset.modInit = 'true';
  try {
    const mod = await organism.loader();
    const ModuleClass = mod.default;
    if (organism.render && typeof organism.render === 'function') {
      organism.render(ModuleClass, [node]);
    } else {
      // eslint-disable-next-line no-new
      new ModuleClass(node, organism.props);
    }
  } catch (error) {
    console.error(`Failed to import module "${organism.name}":`, error);
  }
}

function initDeferredOnViewport(defs) {
  if (!('IntersectionObserver' in window)) {
    create(defs); // no IO support → fall back to eager init (previous behaviour)
    return;
  }
  defs.forEach((organism) => {
    const nodes = Array.from(document.querySelectorAll(`[data-module="${organism.name}"]`))
      .filter((node) => node.dataset.modInit !== 'true');
    if (!nodes.length) return;
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        initDeferredModuleOnNode(organism, entry.target);
      });
    }, { rootMargin: '300px 0px' });
    nodes.forEach((node) => io.observe(node));
  });
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    create(modules.filter((m) => !DEFER_MODULES.has(m.name)));
    initDeferredOnViewport(modules.filter((m) => DEFER_MODULES.has(m.name)));
  },
  false,
);

// Gutenberg / ACF editor: block previews are rendered (and re-rendered on every
// edit) asynchronously AFTER DOMContentLoaded, so the one-shot init above never
// binds to them. Re-run init scoped to each preview as ACF (re-)renders it — the
// data-mod-init guard in importModule keeps already-initialized nodes from
// double-binding.
// Carousels / sliders (Slick, or our own DOM-cloning loop) rewrite the markup
// that Gutenberg's React owns inside a block preview — wrapping slides in
// .slick-track, cloning nodes, etc. On the next re-render React tries to
// reconcile nodes that were moved/removed and throws
// "NotFoundError: Failed to execute 'removeChild'…", which shows as
// "This block has encountered an error and cannot be previewed". Skip these in
// the editor; the static server-rendered markup is enough for the preview (they
// still initialize normally on the front end).
const EDITOR_SKIP_MODULES = new Set([
  'ModSlider', 'ModCarouselSlide', 'LogoSlide', 'TestimonialsCarousel',
  'ResultCarousel', 'BrandLogo', 'IconCards', 'UpcomingEvents', 'Testimonial',
  'CardLarge', 'FlipCards', 'VideoGridContent', 'CultureGallery',
  'PartnerCaseStudies',
]);

if (typeof window !== 'undefined' && window.acf && typeof window.acf.addAction === 'function') {
  window.acf.addAction('render_block_preview', ($el) => {
    const node = $el && $el.length ? $el[0] : ($el instanceof HTMLElement ? $el : null);
    if (node) create(modules.filter((m) => !EDITOR_SKIP_MODULES.has(m.name)), node);
  });
}

// Hot reload for start dev.
if (import.meta.hot) {
  console.log('Updated vite');
}